import type { Coordinate } from 'ol/coordinate';
import type { FeatureLike } from 'ol/Feature';
import Feature from 'ol/Feature';
import OLPoint from 'ol/geom/Point.js';
import VectorLayer from 'ol/layer/Vector';
import * as proj from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';

import type { TrackPropertiesDTO } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import type { PositionResponse } from '@nora-monorepo/track-api-web-client';
import { useGenericUserStore, useWebViewStore } from '@nora-monorepo/ui-shared';

import { MilSymbolStyleManager } from '#nora-ui/map/drawtools/styles/milsymbol-style';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { getLocationFeatureStyle, getOwnLocationFeatureStyle } from '#nora-ui/map/feature-styles/location-feature-style';
import { FeatureStyleManager } from '#nora-ui/map/map/FeatureStyleManager';
import { ClusterableLayer } from '#nora-ui/map/map/layer-managers/ClusterableLayer';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import type { OpenLayersTrackProperties, TrackProperties } from '#nora-ui/map/types/track.types';
import { TrackVisibilityState } from '#nora-ui/map/types/track.types';

export class TrackLayerManager extends ClusterableLayer {
  readonly LAYER_ID = 'trackLayer';
  readonly LAYER_NAME = 'NORA Tracks';
  override readonly isClickable = true;

  private userStore = useGenericUserStore();
  private webViewStore = useWebViewStore();

  // Default icon+label styles keyed by call sign, so we don't rebuild them on every render.
  private readonly defaultTrackStyleCache = new Map<string, Style[]>();

  private readonly LINE_WIDTH_DEFAULT = 1;
  private readonly LINE_WIDTH_HIGHLIGHTED = 3;

  protected override LAYER_Z_INDEX = 9999;

  protected override CLUSTER_ICON_NAME = 'person';

  // Separate layer for current user so they're never clustered
  private currentUserSource = new VectorSource();
  private currentUserLayer = new VectorLayer({
    source: this.currentUserSource,
    zIndex: 10000,
    updateWhileAnimating: true,
    style: (feature: FeatureLike) => this.getStyleForSingleFeature(feature),
  });

  // rAF handle so a burst of position updates coalesces into one redraw per frame.
  private pendingRenderHandle: number | null = null;

  constructor(map: NoraMap) {
    super(map);
  }

  override async setup(): Promise<void> {
    await super.setup();
    this.map.getOlMapOrThrow().addLayer(this.currentUserLayer);
    this.setupPanHandler();
  }

  /**
   * Load/reload track layer features from last known positions.
   * Clears existing features before loading.
   */
  override load(): void {
    this.unload();

    const lastKnownTracks = this.map.state.track.getAllLastKnownTracks();

    for (const [producerId, track] of Object.entries(lastKnownTracks)) {
      const visibilityState = this.map.state.track.getTrackVisibilityState(producerId);

      if (visibilityState !== TrackVisibilityState.Hidden) {
        this.handleIncomingTrack(track.position, track.properties ?? {});
      }
    }
  }

  /**
   * Unload/clear all track layer features.
   */
  override unload(): void {
    this.cancelScheduledRender();
    this.source.clear();
    this.currentUserSource.clear();
  }

  /**
   * Batches redraws so a burst of incoming updates only triggers one render on the next frame.
   */
  private scheduleRender(): void {
    if (this.pendingRenderHandle !== null) {
      return;
    }

    this.pendingRenderHandle = requestAnimationFrame(() => {
      this.pendingRenderHandle = null;
      this.source.changed();
      this.currentUserSource.changed();
    });
  }

  private cancelScheduledRender(): void {
    if (this.pendingRenderHandle !== null) {
      cancelAnimationFrame(this.pendingRenderHandle);
      this.pendingRenderHandle = null;
    }
  }

  private getVisibleFeatures(features: FeatureLike[]): FeatureLike[] {
    return features.filter(
      (feature: FeatureLike) =>
        feature.get('visibilityState') !== TrackVisibilityState.Hidden,
    );
  }

  protected override getFeatures(feature: FeatureLike): FeatureLike[] {
    const features = feature.get('features') as FeatureLike[] | undefined;

    if (!features) {
      return [];
    }

    return this.getVisibleFeatures(features);
  }

  protected override getStyleForSingleFeature(feature: FeatureLike): Style | Style[] {
    const { visibilityState, callSign, symbolId, symbolPackId, sidc } = getFeatureProperties<TrackProperties>(feature);

    if (visibilityState === TrackVisibilityState.Hidden) {
      return new Style();
    }

    const userId = feature.getId() as string;

    const lastKnownLocation = this.map.state.track.getLastKnownTrackForProducerId(userId);

    if (this.userStore.isCurrentUser(userId)) {
      // Only in the webview, ergo on the device you are sharing location from, show accuracy and heading
      if (this.webViewStore.isWebView) {
        return getOwnLocationFeatureStyle({
          accuracy: {
            show: lastKnownLocation?.position.locationAccuracy != null,
            value: lastKnownLocation?.position.locationAccuracy ?? 0,
          },
          heading: {
            show: lastKnownLocation?.position.heading != null,
            value: lastKnownLocation?.position.heading ?? 0,
          },
        });
      }

      return getOwnLocationFeatureStyle();
    }

    // If the track has a SIDC, attempt to render a milsymbol. If that fails (e.g. invalid SIDC), fall back to regular rendering.
    if (isNonEmptyString(sidc)) {
      const milSymbol = MilSymbolStyleManager.getMilSymbolGraphic({
        sidc,
        heading: lastKnownLocation?.position.heading ?? undefined,
        speed: lastKnownLocation?.position.speed ?? undefined,
        callSign,
      });

      if (milSymbol) {
        return milSymbol;
      }
    }

    const hasCustomSymbol = isNonEmptyString(symbolPackId) && isNonEmptyString(symbolId);

    // Not cached: the symbol image loads async, so a cached placeholder would never update.
    if (hasCustomSymbol) {
      const locationFeatureIcon = getLocationFeatureStyle({
        text: callSign,
        symbolPackId,
        symbolId,
        onLoad: () => this.scheduleRender(),
      });

      return FeatureStyleManager.addStaticLabelWithText(callSign ?? '', locationFeatureIcon);
    }

    return this.getDefaultTrackStyle(callSign ?? '');
  }

  /**
   * Cached default icon + label style for a call sign.
   */
  private getDefaultTrackStyle(callSign: string): Style[] {
    const cached = this.defaultTrackStyleCache.get(callSign);

    if (cached) {
      return cached;
    }

    const locationFeatureIcon = getLocationFeatureStyle({ text: callSign });
    const styles = FeatureStyleManager.addStaticLabelWithText(callSign, locationFeatureIcon);

    this.defaultTrackStyleCache.set(callSign, styles);

    return styles;
  }

  /**
   * Handles incoming track updates.
   */
  handleIncomingTrack(
    position: PositionResponse,
    properties: TrackProperties,
  ): void {
    const producerId = position.producerId;

    const trackIsVisible = this.map.state.track.isTrackVisible(producerId);

    if (!trackIsVisible) {
      return;
    }

    this.updateOrCreateTrackPosition(producerId, position, {
      ...properties,
      visibilityState: TrackVisibilityState.Visible,
    });

    if (this.map.ref.followedProducerId === producerId) {
      this.map.setCenter({
        latitude: position.latitude,
        longitude: position.longitude,
      });
    }
  }

  /**
   * Update or add a track feature on the map.
   */
  private updateOrCreateTrackPosition(
    userId: string,
    position: PositionResponse,
    properties?: TrackProperties,
  ): void {
    const trackFeature = this.getTrackFeatureByUserId(userId);
    const webMercatorCoords = proj.transform(
      [position.longitude, position.latitude],
      MapSetup.WGS84,
      MapSetup.WEB_MERCATOR,
    );

    if (trackFeature) {
      this.updateTrackCoordinates(userId, webMercatorCoords);

      if (properties) {
        trackFeature.setProperties(this.getOpenLayersTrackProperties(properties));
        this.scheduleRender();
      }
    }
    else {
      this.addTrackFeature(userId, webMercatorCoords, properties);
    }
  }

  /**
   * Adds a track feature to the map.
   */
  private addTrackFeature(
    userId: string,
    coordinates: Coordinate,
    properties?: TrackProperties,
  ): void {
    const pointFeature = new Feature({
      id: userId,
      geometry: new OLPoint(coordinates),
    });

    pointFeature.setId(userId);

    if (properties) {
      pointFeature.setProperties(this.getOpenLayersTrackProperties(properties));
    }

    this.getVectorSourceForUser(userId).addFeature(pointFeature);
  }

  /**
   * Updates the coordinates of a track feature.
   */
  private updateTrackCoordinates(userId: string, coordinates: Coordinate): void {
    const trackFeature = this.getTrackFeatureByUserId(userId);

    if (!trackFeature) {
      return;
    }

    const point = trackFeature.getGeometry() as OLPoint;
    point.setCoordinates(
      coordinates,
    );
  }

  /**
   * Gets a track feature by user ID, checking both the current user layer and the clustered layer.
   */
  private getTrackFeatureByUserId(userId: string): Feature | null {
    return this.currentUserSource.getFeatureById(userId)
      ?? this.source.getFeatureById(userId);
  }

  private getVectorSourceForUser(userId: string): VectorSource {
    return this.userStore.isCurrentUser(userId) ? this.currentUserSource : this.source;
  }

  /**
   * Sets the visibility state of a track, removing it from the map if set to hidden.
   */
  setTrackVisibility(
    userId: string,
    visibilityState: TrackVisibilityState,
  ): void {
    const trackFeature = this.getTrackFeatureByUserId(userId);

    if (!trackFeature) {
      const lastKnownLocation = this.map.state.track.getLastKnownTrackForProducerId(userId);

      if (!lastKnownLocation) {
        return;
      }

      if (visibilityState === TrackVisibilityState.Hidden) {
        return;
      }

      this.updateOrCreateTrackPosition(
        userId,
        lastKnownLocation.position,
        {
          ...lastKnownLocation.properties,
          visibilityState,
        },
      );

      return;
    }

    if (visibilityState === TrackVisibilityState.Hidden) {
      this.getVectorSourceForUser(userId).removeFeature(trackFeature);

      return;
    }

    trackFeature.setProperties(
      this.getOpenLayersTrackProperties({
        ...trackFeature.getProperties(),
        visibilityState,
      }),
    );

    this.scheduleRender();
  }

  /**
   * Clears the track for a specific user, optionally filtering by device ID.
   * @param userId
   * @param deviceId
   */
  clearUserTrack(userId: string, deviceId?: string): void {
    if (isNonEmptyString(deviceId)) {
      const lastKnownTrack = this.map.state.track.getLastKnownTrackForProducerId(userId);

      if (lastKnownTrack?.position.properties) {
        const properties: TrackPropertiesDTO = lastKnownTrack.position.properties;

        if (properties.deviceId !== deviceId) {
          return;
        }
      }
    }

    // Realtime updates can leave a producer in either source, so clear both
    // to avoid leaving an own-user marker behind after a source transition.
    for (const source of [this.currentUserSource, this.source]) {
      const trackFeature = source.getFeatureById(userId);

      if (trackFeature) {
        source.removeFeature(trackFeature);
      }
    }
  }

  private setupPanHandler() {
    const olMap = this.map.getOlMapOrThrow();

    olMap.on('movestart', () => {
      const isAnimating = olMap.getView().getAnimating();

      if (isAnimating) {
        return;
      }

      this.map.ref.stopFollowing();
      this.map.ref.setHeadingUpMode(false);
    });
  }

  /**
   * Gets the OpenLayers track properties from the generic track properties.
   */
  private getOpenLayersTrackProperties(trackProperties: TrackProperties): OpenLayersTrackProperties {
    const lineWidth = trackProperties.visibilityState === TrackVisibilityState.Highlighted ? this.LINE_WIDTH_HIGHLIGHTED : this.LINE_WIDTH_DEFAULT;

    const formattedTrack: OpenLayersTrackProperties = {
      ...trackProperties,
      fill_color: 'blue',
      line_width: lineWidth,
      label_text: trackProperties.callSign ?? '',
    };

    return formattedTrack;
  }
}
